// Original sound (BRIEF 7.3): the SFU is a pass-through, so codec
// parameters are decided by the sending client's own SDP. Getting Opus into
// "music mode" (stereo, no DTX, a real bitrate) means writing an a=fmtp line
// for the audio section's Opus payload type before the offer is sent.
//
// This is pure string manipulation with no WebRTC types in it on purpose:
// it has to run identically wherever an SDP string shows up (browser,
// worker, a future recorder), and it has to be testable with plain
// `node --test`, with no DOM and no RTCPeerConnection in the room.

export interface OpusMungeOptions {
  readonly bitrateBps: number;
  readonly stereo: boolean;
  /** Discontinuous transmission. Off for music. */
  readonly dtx: boolean;
  /** Forward error correction. */
  readonly fec: boolean;
}

const RTPMAP_OPUS = /^a=rtpmap:(\d+)\s+opus\/\d+(?:\/\d+)?$/i;
const FMTP = /^a=fmtp:(\d+)\s+(.*)$/i;
const MID_LINE = /^a=mid:(.+)$/i;

/** Returns [start, end) line indices of the m=audio section, or null if the
 * SDP has none. `end` is exclusive: either the next m= line or the array
 * length. */
function findAudioSection(lines: readonly string[]): [number, number] | null {
  const start = lines.findIndex((line) => line.startsWith("m=audio"));
  if (start === -1) return null;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (lines[i]?.startsWith("m=")) {
      end = i;
      break;
    }
  }
  return [start, end];
}

/** Every [start, end) span for each `m=audio` section in `sdp`, in document
 * order. A single connection PULLING several participants' audio has one
 * `m=audio` section per pulled track, so a caller that needs to touch one
 * specific participant's section (rather than "the first audio section",
 * which is all `findAudioSection` ever looks at) walks this list and
 * matches by mid. */
function findAllAudioSections(lines: readonly string[]): Array<[number, number]> {
  const sections: Array<[number, number]> = [];
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i]?.startsWith("m=audio")) continue;
    let end = lines.length;
    for (let j = i + 1; j < lines.length; j++) {
      if (lines[j]?.startsWith("m=")) {
        end = j;
        break;
      }
    }
    sections.push([i, end]);
  }
  return sections;
}

function sectionMid(lines: readonly string[], start: number, end: number): string | null {
  for (let i = start; i < end; i++) {
    const match = MID_LINE.exec(lines[i] ?? "");
    if (match?.[1]) return match[1].trim();
  }
  return null;
}

function parseFmtpParams(body: string): Record<string, string> {
  const params: Record<string, string> = {};
  for (const part of body.split(";")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf("=");
    if (eq === -1) {
      params[trimmed] = "";
    } else {
      params[trimmed.slice(0, eq)] = trimmed.slice(eq + 1);
    }
  }
  return params;
}

function serializeFmtpParams(params: Record<string, string>): string {
  return Object.keys(params)
    .sort()
    .map((key) => (params[key] === "" ? key : `${key}=${params[key]}`))
    .join(";");
}

/** Rewrites (or adds) the Opus fmtp line inside `lines[start, end)`,
 * mutating `lines` in place. Returns true if it found an Opus payload type
 * to munge, false (no-op) if this section has none. Shared by the
 * single-section and mid-scoped munging functions below so the actual fmtp
 * rewrite logic lives in exactly one place. */
function mungeSection(lines: string[], start: number, end: number, opts: OpusMungeOptions): boolean {
  let opusPayloadType: string | null = null;
  let rtpmapIndex = -1;
  for (let i = start; i < end; i++) {
    const match = RTPMAP_OPUS.exec(lines[i] ?? "");
    if (match?.[1]) {
      opusPayloadType = match[1];
      rtpmapIndex = i;
      break;
    }
  }
  if (!opusPayloadType) return false;

  let fmtpIndex = -1;
  let existingParams: Record<string, string> = {};
  for (let i = start; i < end; i++) {
    const match = FMTP.exec(lines[i] ?? "");
    if (match && match[1] === opusPayloadType) {
      fmtpIndex = i;
      existingParams = parseFmtpParams(match[2] ?? "");
      break;
    }
  }

  const params: Record<string, string> = {
    ...existingParams,
    stereo: opts.stereo ? "1" : "0",
    "sprop-stereo": opts.stereo ? "1" : "0",
    maxaveragebitrate: String(opts.bitrateBps),
    usedtx: opts.dtx ? "1" : "0",
    useinbandfec: opts.fec ? "1" : "0",
  };
  const fmtpLine = `a=fmtp:${opusPayloadType} ${serializeFmtpParams(params)}`;

  if (fmtpIndex !== -1) {
    lines[fmtpIndex] = fmtpLine;
  } else {
    lines.splice(rtpmapIndex + 1, 0, fmtpLine);
  }
  return true;
}

/**
 * Rewrites (or adds) the Opus fmtp line inside the FIRST audio section of
 * `sdp` so the encoder runs in music mode instead of voice mode. Idempotent:
 * applying it twice with the same options produces the same SDP. If the SDP
 * has no audio section or no Opus payload, it is returned unchanged.
 *
 * This only ever touches one section, which is correct for a client's own
 * PUBLISH offer (one local mic, one `m=audio` section) but not for a PULL
 * connection, which can carry one `m=audio` section per remote participant's
 * audio track on the same PeerConnection — see
 * `applyOriginalSoundMungingToMids` for that case.
 */
export function applyOriginalSoundMunging(sdp: string, opts: OpusMungeOptions): string {
  const lines = sdp.split(/\r\n|\n/);
  const section = findAudioSection(lines);
  if (!section) return sdp;
  const [start, end] = section;
  const changed = mungeSection(lines, start, end, opts);
  return changed ? lines.join("\r\n") : sdp;
}

/**
 * The pull-side counterpart to `applyOriginalSoundMunging`: rewrites the
 * Opus fmtp line only inside the `m=audio` sections whose `a=mid:` matches
 * one of `mids`, leaving every other section (this client's own published
 * audio, or another participant's pulled audio that didn't ask for original
 * sound) untouched. Idempotent, and a no-op if `mids` is empty or none of
 * them match an audio section actually present in `sdp`.
 */
export function applyOriginalSoundMungingToMids(
  sdp: string,
  mids: readonly string[],
  opts: OpusMungeOptions,
): string {
  if (mids.length === 0) return sdp;
  const wanted = new Set(mids);
  const lines = sdp.split(/\r\n|\n/);
  const sections = findAllAudioSections(lines).filter(([start, end]) => {
    const mid = sectionMid(lines, start, end);
    return mid !== null && wanted.has(mid);
  });
  if (sections.length === 0) return sdp;

  let changedAny = false;
  // Splicing a new fmtp line into one section shifts every line index after
  // it, so a later section's own precomputed [start, end) would go stale if
  // we mutated earliest-first. Processing last-to-first means every section
  // still being touched is entirely before whatever was just spliced.
  for (const [start, end] of [...sections].reverse()) {
    if (mungeSection(lines, start, end, opts)) changedAny = true;
  }
  return changedAny ? lines.join("\r\n") : sdp;
}

/** True if `sdp` already carries the Opus music-mode parameters, so a caller
 * can avoid re-munging an SDP that was already produced by this module. */
export function hasOriginalSoundApplied(sdp: string): boolean {
  const lines = sdp.split(/\r\n|\n/);
  const section = findAudioSection(lines);
  if (!section) return false;
  const [start, end] = section;
  for (let i = start; i < end; i++) {
    const match = FMTP.exec(lines[i] ?? "");
    if (match) {
      const params = parseFmtpParams(match[2] ?? "");
      if (params.stereo === "1" && params["sprop-stereo"] === "1" && params.maxaveragebitrate) {
        return true;
      }
    }
  }
  return false;
}
